<template>
  <el-container class="checkout">
    <el-container class="main-container">
      <el-main
        class="main-content"
        v-loading="loading"
      >
        <div class="content-header">
          <h2>
            <span class="currency">$</span> {{ $t('checkout.title') }}
          </h2>
        </div>
        <div
          class="content"
          v-if="!loading"
        >
          <div class="order-summary">
            <table>
              <thead>
                <tr>
                  <th>{{ $t('checkout.orderSummary.id') }}</th>
                  <th>{{ $t('checkout.orderSummary.severity') }}</th>
                  <th>{{ $t('checkout.orderSummary.vulnerability') }}</th>
                  <th>{{ $t('checkout.orderSummary.price') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item) in orderItems"
                  :key="item.id"
                >
                  <td>{{ item.vulId?.slice(0, 8) }}</td>
                  <td :class="item.severity">{{ item.severity }}</td>
                  <td>{{ item.category }}</td>
                  <td>{{formatCurrency(item) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="totals">
              <div class="totals-row total">
                <span class="label">{{ $t('checkout.orderSummary.total') }}</span>
                <span class="currencys">{{ orderDetail.currency?.toUpperCase() || 'USD' }}</span>
                <span class="amount">{{formatCurrency(orderDetail) }}</span>
              </div>
            </div>
          </div>
          <div class="payment-methods">
            <div class="methods">
              <div class="method">
                <img
                  src="../../../assets/codeScan/WechatPay.png"
                  alt="WechatPay"
                />
              </div>
              <div class="method">
                <img
                  src="../../../assets/codeScan/stripe.png"
                  alt="stripe"
                />
              </div>
              <div class="method">
                <img
                  src="../../../assets/codeScan/zfb.png"
                  alt="Alipay"
                />
              </div>
            </div>
            <p class="or-text">{{ $t('checkout.paymentMethods.or') }}</p>
            <div class="credit-card">
              <h2>
                <img
                  src="../../../assets/codeScan/ze-checked.png"
                  alt="Visa"
                />
                {{ $t('checkout.paymentMethods.creditCard') }}
              </h2>
              <StripeComponent
                ref="strip"
                v-if="clientSecret && !loading"
                :clientSecret="clientSecret"
              />
              <div class="remember">
                <el-button
                  :loading="payLoading"
                  type="primary"
                  @click="payNow"
                >{{ $t('checkout.paymentMethods.payNow') }}</el-button>
                <el-button
                  class="cancel-button"
                  v-if="!payLoading"
                  type="text"
                  :loading="cancelLoading"
                  @click="handleCancel"
                >{{ $t('checkout.paymentMethods.cancelPayment') }}</el-button>
              </div>
            </div>
          </div>
        </div>
      </el-main>
    </el-container>
    <el-dialog
      :visible="dialogVisible"
      :title="$t('checkout.paymentMethods.cancelConfirmTitle')"
      width="30%"
    >
      <span>{{ $t('checkout.paymentMethods.cancelConfirm') }}</span>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">{{ $t('checkout.paymentMethods.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="cancelPayment"
          >{{ $t('checkout.paymentMethods.confirm') }}</el-button>
        </span>
      </template>
    </el-dialog>
  </el-container>
</template>

<script>
import StripeComponent from '@/components/stripe.vue';
import { getOrderDetail, cancelOrder } from '@/api/base';
import { stripeInit } from '@/lib/stripe';
import { formatCurrency } from '@/utils/currency';

export default {
  name: 'ReportsIndex',
  components: {
    StripeComponent,
  },
  data() {
    return {
      orderItems: [],
      subtotal: 24000,
      tax: 2000,
      total: 26000,
      orderId: this.$route.query.orderId,
      clientSecret: '',
      orderDetail: null,
      loading: true,
      payLoading: false,
      cancelLoading: false,
      taskId: this.$route.query.taskId,
      dialogVisible: false,
      paymentIntentId: '',
    };
  },
  created() {
    const { orderId } = this.$route.query;

    Promise.all([getOrderDetail(orderId), stripeInit()]).then(([res]) => {
      this.orderDetail = res;
      this.loading = false;
      this.orderItems = res.vulProducts;
      this.clientSecret = res.paymentIntentClientSecret;
      this.paymentIntentId = res.paymentIntentId;
    });
  },
  methods: {
    formatCurrency(row) {
      const currency = row.currency?.toUpperCase() || 'USD';
      const locale = currency === 'CNY' ? 'zh-CN' : 'en-US';

      return formatCurrency(row.amount, { currency, conversionFactor: 100, locale });
    },

    payNow() {
      if (!this.$refs.strip.elements) {
        return;
      }

      this.payLoading = true;

      const { href } = this.$router.resolve({ name: 'PaySuccessful', query: { taskId: this.taskId } });

      this.$stripe
        .confirmPayment({
          elements: this.$refs.strip.elements,
          confirmParams: {
            return_url: `${window.location.origin}${href}`,
          },
        })
        .finally(() => {
          this.payLoading = false;
        });
    },
    handleCancel() {
      this.dialogVisible = true;
    },
    cancelPayment() {
      this.cancelLoading = true;
      this.dialogVisible = false;
      cancelOrder(this.orderId)
        .then(() => {
          this.$message.success(this.$t('checkout.paymentMethods.cancelSuccess'));

          this.$nextTick(() => {
            this.$router.push({ name: 'ReportsView', query: { taskId: this.taskId } });
          });
        })
        .finally(() => {
          this.cancelLoading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.checkout {
  width: 100%;
  display: flex;

  .currency {
    --width: 23px;
    margin-right: 8px;
    display: inline-block;
    width: var(--width);
    height: var(--width);
    background: #000;
    color: #fff;
    border-radius: 100%;
    line-height: var(--width);
    text-align: center;
    font-size: 16px;
  }

  .main-container {
    display: flex;
    flex-direction: column;
    width: calc(100% - 80px);
  }

  .logo {
    width: 150px;
  }

  .user-info {
    display: flex;
    align-items: center;
  }

  .username {
    margin-right: 10px;
  }

  .user-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
  }

  .sidebar {
    height: calc(100vh - 85px);
    margin: 50px 0px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
  }

  .el-menu {
    background: linear-gradient(to bottom, #e9eef2, #fff);
    height: calc(100vh - 85px);
    border-radius: 10px;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    background-color: #fff;
    margin: 0;
    border-radius: 10px;
    width: 100%;
    position: absolute;
    top: 0px;
    right: 0px;
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .main-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    background: linear-gradient(to bottom, #e9eef2, #fff);
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    background-color: #f6f6f6;
    border-radius: 10px;
    padding: 0px 20px;
    height: 55px;
    line-height: 54px;

    h2 {
      display: flex;
      align-items: center;
      font-size: 20px;
      padding-left: 10px;
    }

    img {
      width: 30px;
      height: 30px;
      position: relative;
      top: 7px;
      right: 5px;
    }
  }

  .content {
    flex: 1;
    display: flex;
    width: 100%;
    margin-top: 20px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 8px;

    .order-summary {
      display: flex;
      flex-direction: column;
      padding: 20px;
      background-color: #f6f6f6;
      margin: 30px 30px;
      border-radius: 10px;
    }

    .payment-methods {
      margin-top: 20px;

      .method {
        padding: 0px 10px;
      }
    }

    .order-summary table {
      width: 100%;
      border-collapse: collapse;
    }

    .order-summary th,
    .order-summary td {
      padding: 10px;
      text-align: left;
    }

    .order-summary th {
      background-color: #f5f5f5;
    }

    .order-summary .totals {
      margin-top: auto;
      display: grid;
      gap: 5px;
      .totals-row {
        display: grid;
        grid-template-columns: auto auto auto;
        justify-content: end;
        text-align: right;
        font-size: 20px;
        margin-bottom: 10px;
        line-height: 40px;

        .label {
          width: 120px;
          text-align: center;
        }

        .currency {
          margin-right: 20px;
          font-size: 17px;
        }

        .amount {
          flex: 0 0 auto;
          text-align: right;
        }
      }

      .totals-row.total {
        font-size: 30px;
        font-weight: bold;

        .label {
          width: 100px;
          text-align: center;
        }

        .currencys {
          flex: 0 0 auto;
          margin-right: 10px;
          font-size: 25px;
        }

        .amount {
          flex: 0 0 auto;
          text-align: right;
        }
      }
    }

    .payment-methods .methods {
      display: flex;
      justify-content: center;
      margin-top: 20px;
      align-items: center;
    }

    .payment-methods .method img {
      width: 65px;
    }

    .or-text {
      display: flex;
      align-items: center;
      text-align: center;
      color: #9e9a9a;
      margin: 0 0;
    }

    .or-text::before,
    .or-text::after {
      content: '';
      border-bottom: 1px solid #dddddd;
      margin: 0 10px;
      width: 190px;
    }

    .credit-card {
      margin: 0px 30px;

      h2 {
        font-size: 22px;
      }

      img {
        width: 20px;
        height: 20px;
        position: relative;
        top: 2px;
      }

      .remember {
        margin: 20px 10px;
        color: #2b2b2b;
        font-weight: 600;

        button {
          height: 55px;
          font-size: 20px;
        }

        .cancel-button {
          color: #2b2b2b;
          font-weight: 600;
          height: 40px;
          font-size: 15px;
        }
      }
    }

    .credit-card .card-icons img {
      width: 50px;
      height: 35px;
      margin-right: 20px;
    }

    .el-input {
      margin-top: 10px;
    }

    .el-checkbox {
      margin-top: 10px;
      font-size: 20px;
      span {
        color: #2b2b2b;
        font-weight: 600;
        font-size: 15px;
      }

      .el-checkbox__inner {
        border: 1px solid #000000;
        width: 16px;
        height: 16px;
      }
    }
    .el-button {
      margin-top: 20px;
      width: 100%;
    }
  }
}
</style>